import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X, User, Phone, Calendar, MailCheck, ChevronLeft, ChevronRight } from 'lucide-react';

// Images Import
import img1 from "../assets/services/car.jpeg";
import img2 from "../assets/services/employee.jpeg";
import img3 from "../assets/services/overseas.jpeg";    
import img4 from "../assets/services/corp.jpeg";
import img5 from "../assets/services/plane.jpeg";
import img6 from "../assets/services/driver.jpeg";
import img7 from "../assets/services/event.jpeg";
import img8 from "../assets/services/intercity.jpeg";

const Services = () => {
  const [activeService, setActiveService] = useState(null);
  const [bookingStep, setBookingStep] = useState('details'); 
  const [currentIndex, setCurrentIndex] = useState(2); 
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    if (activeService) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    
    return () => window.removeEventListener('resize', checkMobile);
  }, [activeService]);

  const services = [
    { title: 'Luxury Car Rental', image: img1, description: 'Experience the ultimate comfort and style with our premium fleet of luxury sedans and SUVs.', features: ['Chauffeur Driven', 'Latest Models', 'GPS Tracking', 'Events and Weddings'] },
    { title: 'Corporate Travel', image: img2, description: 'Tailored mobility solutions for businesses, ensuring your executives travel with punctuality and class.', features: ['Tech enabled mobility', 'Priority Booking', '24/7', 'Professional Attire Drivers'] },
    { title: 'Overseas', image: img3, description: 'Specialized security-integrated transportation for dignitaries and high-net-worth individuals.', features: ['Armed/Unarmed Guards', 'Discreet Operations', 'Route Survey', 'Bulletproof Vehicle Option'] },
    { title: 'Employee Transport', image: img4, description: 'Safe and efficient daily commute solutions for your entire workforce with real-time tracking.', features: ['Women Safety', 'Panic Button Enabled', 'Tech enabled functions', '24/7 Control Room'] },
    { title: 'Airport Transfers', image: img5, description: 'Reliable pick-and-drop services to ensure you never miss a flight or wait at the arrivals.', features: ['Flight Monitoring', 'Meet & Greet', 'Luggage Assistance', 'On-time Guarantee'] },
    { title: 'VIP Concierge', image: img6, description: 'High-end personalized assistance for VVIPs, celebrities, and diplomats.', features: ['Security Detail', 'Discreet Service', 'Red Carpet Arrival', 'Personal Coordinator'] },
    { title: 'Events Logistics', image: img7, description: 'End-to-end fleet management for large-scale conferences and weddings.', features: ['On-site Coordinator', 'Help Desk Setup', 'Multiple Car Options', 'Custom Branding'] },
    { title: 'Intercity Trips', image: img8, description: 'Comfortable and safe long-distance travel across India with experienced highway drivers.', features: ['Inter-city Permits', 'Well-rested Drivers', 'Luxury Coaches', 'Flexible Itinerary'] }
  ];

  const handleNext = () => setCurrentIndex((prev) => (prev + 1) % services.length);
  const handlePrev = () => setCurrentIndex((prev) => (prev - 1 + services.length) % services.length);

  const closeModal = () => {
    setActiveService(null);
    setBookingStep('details');
  };

  return (
    <section id="services" className="py-16 md:py-24 bg-white scroll-mt-20 overflow-hidden w-full">
      <div className="container mx-auto px-4 text-center mb-12">
        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} className="text-3xl md:text-5xl font-black text-gray-900 mb-4 uppercase tracking-tighter">
          Our <span className="text-blue-600">Services</span>
        </motion.h2>
        <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto italic leading-relaxed px-4">Elite standards in corporate and luxury travel.</p>
      </div>

      <div className="relative w-full h-[480px] md:h-[550px] flex items-center justify-center">
        <div className="relative w-full h-full flex items-center justify-center">
          <AnimatePresence initial={false}>
            {services.map((service, index) => {
              let position = index - currentIndex;
              if (position < -Math.floor(services.length / 2)) position += services.length;
              if (position > Math.floor(services.length / 2)) position -= services.length;
              
              if (Math.abs(position) > (isMobile ? 1 : 3)) return null;
              const isCenter = position === 0;

              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{
                    x: position * (isMobile ? 220 : 300), 
                    scale: isCenter ? 1 : 0.85,
                    zIndex: 10 - Math.abs(position),
                    opacity: 1 - Math.abs(position) * 0.3,
                  }}
                  transition={{ type: "spring", stiffness: 260, damping: 25 }}
                  onClick={() => isCenter ? setActiveService(service) : setCurrentIndex(index)}
                  className={`absolute w-[260px] md:w-[380px] bg-white shadow-2xl rounded-2xl overflow-hidden cursor-pointer border border-slate-100 ${isCenter ? 'ring-8 ring-blue-600/5' : ''}`}
                >
                  <div className="h-48 md:h-80 relative">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
                    {!isCenter && <div className="absolute inset-0 bg-black/20" />}
                  </div>
                  <div className="p-5 md:p-6 bg-slate-50/50 text-left">
                    <h3 className="text-base md:text-xl font-black text-slate-900 mb-2 uppercase tracking-tight">{service.title}</h3>
                    <p className="text-slate-500 text-[10px] md:text-[11px] leading-relaxed line-clamp-2 h-8">{service.description}</p>
                    <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between text-blue-600 font-bold text-[10px] uppercase tracking-widest">
                      <span>View Details</span>
                      <ArrowRight size={14} />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        <button onClick={handlePrev} className="absolute left-2 md:left-12 z-50 bg-white/90 p-3 md:p-4 rounded-full shadow-xl hover:bg-blue-600 hover:text-white transition-all">
          <ChevronLeft size={isMobile ? 20 : 24}/>
        </button>
        <button onClick={handleNext} className="absolute right-2 md:right-12 z-50 bg-white/90 p-3 md:p-4 rounded-full shadow-xl hover:bg-blue-600 hover:text-white transition-all">
          <ChevronRight size={isMobile ? 20 : 24}/>
        </button>
      </div>

      <AnimatePresence>
        {activeService && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-6">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              className="absolute inset-0 bg-slate-900/95 backdrop-blur-md" 
              onClick={closeModal} 
            />
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} 
              animate={{ opacity: 1, scale: 1 }} 
              exit={{ opacity: 0, scale: 0.95 }} 
              className="relative bg-white w-full max-w-4xl shadow-2xl rounded-none flex flex-col md:flex-row overflow-hidden min-h-[500px] md:h-[600px]"
            >
              <button onClick={closeModal} className="absolute top-4 right-4 z-[110] p-2 bg-black/5 hover:bg-black/10 text-gray-500 rounded-full transition-colors">
                <X size={20} />
              </button>

              {/* Image Section - Fixed size to match content */}
              <div className="w-full md:w-1/2 h-48 md:h-full shrink-0">
                <img src={activeService.image} alt={activeService.title} className="w-full h-full object-cover" />
              </div>

              {/* Content Section - Uniform Height & Padding */}
              <div className="w-full md:w-1/2 p-6 md:p-12 flex flex-col bg-white overflow-y-auto">
                <div className="flex-1 flex flex-col justify-center">
                  {bookingStep === 'details' && (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
                      <h2 className="text-2xl md:text-4xl font-black text-gray-900 mb-4 uppercase tracking-tighter">
                        {activeService.title}
                      </h2>
                      <p className="text-gray-600 mb-6 text-sm md:text-base leading-relaxed">
                        {activeService.description}
                      </p>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                        {activeService.features.map((f, i) => (
                          <div key={i} className="flex items-center gap-2 text-[10px] font-bold uppercase text-gray-800">
                            <div className="w-1.5 h-1.5 bg-blue-600" /> {f}
                          </div>
                        ))}
                      </div>

                      <button 
                        onClick={() => setBookingStep('form')} 
                        className="w-full bg-blue-600 text-white py-4 font-black text-xs md:text-sm uppercase tracking-widest hover:bg-gray-900 transition-all rounded-none"
                      >
                        Book This Service
                      </button>
                    </div>
                  )}

                  {bookingStep === 'form' && (
                    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                      <iframe title="Booking Form Submission" name="hidden_iframe" id="hidden_iframe" style={{ display: "none" }}></iframe>
                      <form
                        action="https://formsubmit.co/enquiry@expresstravelcorp.com"
                        method="POST"
                        target="hidden_iframe"
                        onSubmit={() => { setTimeout(() => setBookingStep("success"), 1000); }}
                        className="space-y-4"
                      >
                        <input type="hidden" name="_subject" value={`New Inquiry: ${activeService.title}`} />
                        <input type="hidden" name="_captcha" value="false" />
                        
                        <h3 className="text-xl md:text-2xl font-black text-gray-900 mb-4 uppercase tracking-tight">Booking Details</h3>

                        <div className="space-y-3">
                          <div className="relative border border-gray-200">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                            <input required type="text" placeholder="Full Name" name="name" className="w-full pl-9 pr-4 py-3 bg-white outline-none focus:border-blue-600 text-sm rounded-none" />
                          </div>

                          <div className="relative border border-gray-200">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                            <input required type="tel" placeholder="Phone Number" name="phone" className="w-full pl-9 pr-4 py-3 bg-white outline-none focus:border-blue-600 text-sm rounded-none" />
                          </div>

                          <div className="relative border border-gray-200">
                            <MailCheck className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                            <input required type="email" placeholder="Email Address" name="email" className="w-full pl-9 pr-4 py-3 bg-white outline-none focus:border-blue-600 text-sm rounded-none" />
                          </div>

                          <div className="relative border border-gray-200">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                            <input required type="date" name="date" className="w-full pl-9 pr-4 py-3 bg-white outline-none focus:border-blue-600 text-sm rounded-none" />
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 pt-4">
                          <button type="submit" className="w-full bg-blue-600 text-white font-black py-4 uppercase text-xs md:text-sm tracking-widest rounded-none">
                            Confirm Inquiry
                          </button>
                          <button type="button" onClick={() => setBookingStep('details')} className="w-full py-2 bg-gray-100 font-bold uppercase text-[10px] rounded-none tracking-widest text-gray-600">
                            Back
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {bookingStep === 'success' && (
                    <div className="text-center py-6 animate-in zoom-in duration-300">
                      <div className="w-16 h-16 bg-green-100 text-green-600 rounded-none flex items-center justify-center mx-auto mb-6"><MailCheck size={32}/></div>
                      <h3 className="text-xl md:text-2xl font-black text-gray-900 uppercase mb-2">Request Sent</h3>
                      <p className="text-gray-500 mb-8 italic text-xs md:text-sm">Our team will get back to you shortly.</p>
                      <button onClick={closeModal} className="w-full bg-gray-900 text-white py-4 font-black text-xs uppercase rounded-none tracking-widest">Back to Website</button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Services;